import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Link, useHistory, useParams } from "react-router-dom";
import { signalsData } from "Stores/signalsData";
import SignalsSkeleton from "../SignalsSkeleton";
import { toasterState } from "Components/common/toaster";
import ContactAvatar from "Pages/Dashboard/contacts/ContactAvatar";
import { logoCandidates, isPlaceholderIcon } from "Pages/Dashboard/Accounts/accountLogo";
import SignalIcon from "../SignalIcon";
import SignalSetupDrawer from "../SignalSetupDrawer";
import { Info } from "../Info";
import { initials, HIRING, sellerProfileFor, costUnitOf } from "../signalMeta";
import HiringTable from "./HiringTable";
import { draftEmail } from "../feedActions";
import { fetchTracked, fetchMoves, fetchEstimate, setPaused, markRead, moveOf, CHIPS, chipCounts, nounOf } from "./signalTrackerData";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { AccountLogo } from "../AccountLogo";
import CustomSignalDrawer from "../custom/CustomSignalDrawer";
import { AutomateHost, openAutomate } from "../SignalAutomateDrawer";
import { Evidence } from "../custom/CustomEvidence";
import { customOf, customEventOf, sourcesText, costLine, watchesCompetitors, everyLabel } from "../custom/customSignalMeta";
import "../custom/customSignals.scss";
import "../../agents/agents.scss";
import "../signals.scss";
import "./signalTracker.scss";

const DAY_MS = 24 * 60 * 60 * 1000;
export { Info };
const AGENT_HIDE_KEY = "sg-signal-agent-hidden";
const agentHidden = id => {
  try { return (window.localStorage.getItem(AGENT_HIDE_KEY) || "").split(",").includes(id); } catch (e) { return false; }
};
const hideAgent = id => {
  try { window.localStorage.setItem(AGENT_HIDE_KEY, [...(window.localStorage.getItem(AGENT_HIDE_KEY) || "").split(",").filter(Boolean), id].join(",")); } catch (e) { /* the banner just shows again */ }
};

const shortDate = v => (v && !isNaN(new Date(v)) ? new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "");

const CompanyLogo = ({ domain, name }) => {
  const [failed, setFailed] = useState(false);
  const src = logoCandidates(null, domain)[0];
  return (
    <span className="sg-logo st-logo" aria-hidden="true">
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} onLoad={e => isPlaceholderIcon(e.target) && setFailed(true)} />
      ) : (
        initials(name).slice(0, 1)
      )}
    </span>
  );
};

/** One tracked signal: what it found, how it is watching, and the hand-off to an agent. Mounted at /signals/tracked/:id. */
const SignalTrackerPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const [view, setView] = useState(null);
  const [moves, setMoves] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chip, setChip] = useState(null);
  const [agentOff, setAgentOff] = useState(() => agentHidden(id));
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    const [v, items] = await Promise.all([fetchTracked(id), fetchMoves(id), signalsData.fetchCatalog()]);
    setView(v);
    setMoves(items.map(moveOf));
    setLoading(false);
    if (v) fetchEstimate(v).then(setEstimate);
  };
  useEffect(() => {
    // Moving from one signal to another inside the app: start clean, with this signal's own banner choice.
    setLoading(true);
    setChip(null);
    setAgentOff(agentHidden(id));
    load();
  }, [id]); // eslint-disable-line

  if (loading) {
    return (
      <div className="sg-page">
        <SignalsSkeleton label="Loading tracked signal" />
      </div>
    );
  }
  if (!view || !view.signal) {
    return (
      <div className="sg-page">
        <div className="st-crumbs"><Link to="/signals">Signals</Link></div>
        <div className="sg-feed__empty"><b>This signal is not tracked any more.</b><Link to="/signals">Back to Signals</Link></div>
      </div>
    );
  }

  const s = view.signal;
  const name = view.displayName || s.name || view.signalLabel || "Tracked signal";
  const paused = s.status === "PAUSED";
  const isJobChange = s.signalType === "job_change";
  const isHiring = s.signalType === HIRING;
  const isCustom = s.signalType === "custom";
  const cdef = isCustom ? customOf(view).definition : null;
  const agents = view.agents || [];
  const lastCheck = view.lastCheckedAt || s.lastCheckedAt;
  const nextCheckAt = view.nextCheckAt || s.nextCheckAt;
  // Never checked yet: the refresh runs every 6 hours, so a bare date reads like a missed schedule.
  const nextCheck = !nextCheckAt ? null : lastCheck ? shortDate(nextCheckAt) : "Within 6 hours";
  const scope = view.scopeLabel || "your contacts";
  const recent = typeof view.firedLast30Days === "number" ? view.firedLast30Days : moves.filter(m => m.at > Date.now() - DAY_MS * 30).length;
  const noun = isJobChange ? (recent === 1 ? "move" : "moves") : recent === 1 ? "signal" : "signals";
  const lead = isCustom ? `${cdef.condition || "Your signal"} Across ${scope}.` : isJobChange ? `Everyone in ${scope}, followed to their next company.` : `${view.signalLabel || "This signal"} across ${scope}.`;
  const counts = chipCounts(moves);
  const emptyText = moves.length === 0
    ? isCustom && !lastCheck ? "Nothing yet. The first check is running. Matches land here and in your feed." : `${isHiring ? "No hiring found yet." : isCustom ? "Nothing yet." : "No moves yet."} ${nextCheck ? `Next check ${lastCheck ? `on ${nextCheck}` : "within 6 hours"}.` : "New ones show up here after the next check."}`
    : "Nothing here with this filter.";
  // Lands on what needs you when anything does, else on everything it found.
  const activeChip = chip || (counts.needs > 0 ? "needs" : "all");
  const test = (CHIPS.find(c => c[0] === activeChip) || CHIPS[0])[2];
  const plural = nounOf(s.signalType);
  const chips = CHIPS.filter(([key]) => key === "all" || key === activeChip || counts[key] > 0);
  const shown = moves.filter(test);
  const watching = estimate && typeof estimate.records === "number" ? estimate.records : null;
  const faces = moves.filter((m, i, all) => all.findIndex(o => o.entityId === m.entityId) === i).slice(0, 4);
  const more = watching != null ? Math.max(0, watching - faces.length) : 0;
  const definition = signalsData.catalog.find(d => d.id === s.signalType);
  // Paid signals can be checked on demand (once an hour; the server says when it's too soon).
  // Contact signals are checked on schedule only (the server refuses check-now for them).
  const canCheckNow = !paused && definition && costUnitOf(definition) !== "free" && !isCustom && definition.entityType === "ACCOUNT";
  const checkNow = async () => {
    setChecking(true);
    await signalsData.checkNow(s.id, s.signalType);
    setChecking(false);
    load();
  };
  const cost = isCustom ? costLine({ appliesTo: cdef.appliesTo, records: watching, frequency: customOf(view).frequency, everyDays: customOf(view).everyDays }).split(" · ") : null;
  // The steps "Let an agent do it" opens with (SignalAutomateDrawer's defaults), so the preview matches what follows.
  const steps = isJobChange ? ["Job change", "Stop sequence", "Update contact", "Add to list", "Tell you"] : [(isCustom ? name : view.signalLabel) || "Signal", "Add to list", "Tell you"];

  const read = m => {
    if (!m.unread) return;
    markRead(m.id);
    setMoves(all => all.map(o => (o.id === m.id ? { ...o, unread: false } : o)));
  };
  const open = m => {
    read(m);
    history.push(m.isAccount ? `/accounts/${m.entityId}/overview` : `/contacts/${m.entityId}/activity`);
  };
  // Same choice as the feed: the composer when there is an address, else their page (Find email / Open).
  const act = async m => {
    if (m.main.kind === "draft_email") {
      if (await draftEmail(m.email, history)) read(m);
    } else open(m);
  };
  const actLabel = m => (m.isJobChange && m.main.label === "Draft email" ? "Draft congrats email" : m.main.label);
  const togglePause = () => {
    if (paused) return applyPause();
    confirmationPopupState.setPopupValues({
      content: `Pause “${name}”? It stops checking and uses no credits until you resume it.`,
      actionBtnText: "Pause",
      needCancelBtn: true,
      callback: applyPause
    });
    confirmationPopupState.setShowPopup(true);
  };
  const applyPause = async () => {
    setBusy(true);
    const res = await setPaused(s.id, !paused);
    if (res.ok) {
      toasterState.setToastMsg(paused ? "Tracking resumed" : "Tracking paused", "success");
      setView(await fetchTracked(id));
    } else {
      toasterState.setToastMsg(res.error, "failure");
    }
    setBusy(false);
  };
  const stop = () => {
    confirmationPopupState.setPopupValues({
      content: `Stop tracking “${name}”? Past matches stay in your feed.`,
      actionBtnText: "Stop tracking",
      needCancelBtn: true,
      callback: async () => {
        if (await signalsData.deleteSignal(s.id)) {
          toasterState.setToastMsg("Stopped tracking", "success");
          history.push("/signals");
        }
      }
    });
    confirmationPopupState.setShowPopup(true);
  };
  const closeCustom = savedView => {
    setEditing(false);
    if (savedView) {
      toasterState.setToastMsg("Saved. The next check uses your changes.", "success");
      load();
    }
  };
  const closeDrawer = (saved, savedView) => {
    setEditing(false);
    if (savedView && savedView.signal) openAutomate(savedView);
    else if (saved) load();
  };

  return (
    <div className="sg-page st-page">
      <nav className="st-crumbs" aria-label="Breadcrumb">
        <Link to="/signals">Signals</Link><span aria-hidden="true">›</span><span>{name}</span>
      </nav>
      <div className="st-head">
        <div className="sg-grow">
          <h1>{name}</h1>
          <p className="st-lead">{lead} {recent} {noun} in the last 30 days.</p>
        </div>
        <div className="st-head__acts">
          <button className="sg-link st-ask" onClick={() => import("Stores/copilotStore").then(({ copilotStore }) => copilotStore.askWithQuery(`What should I act on first from my "${name}" signal? Summarize the latest ones.`, { send: false }))}><SignalIcon id="sparkle" size={14} /> Ask Copilot about these</button>
        </div>
      </div>

      {agents.length > 0 ? (
        <div className="st-agentbar is-on">
          <span className="ag-orb ag-orb--sm ag-orb--live" aria-hidden="true"><span className="ag-orb__core" /></span>
          <span className="sg-grow"><b>{agents[0].name || "An agent"}</b> handles each new one as it lands.</span>
          <Link className="sg-link" to={`/agents/${agents[0].id}`}>Open agent</Link>
        </div>
      ) : !agentOff && !watchesCompetitors(view) && (
        <div className="st-agentbar">
          <span className="ag-orb ag-orb--sm ag-orb--rest" aria-hidden="true"><span className="ag-orb__core" /></span>
          <div className="sg-grow">
            <div className="st-agentbar__line"><b>Don't let these wait for you.</b> An agent can pick up each new one the day it lands.</div>
            <div className="st-steps">
              {steps.map((step, i) => (
                <React.Fragment key={step}>
                  {i > 0 && <span className="st-steps__sep" aria-hidden="true">›</span>}
                  <span className="st-step">{step}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
          <button className="sg-btn sg-btn--small" onClick={() => openAutomate(view)}><SignalIcon id="robot" size={15} /> Let an agent do it</button>
          <button className="sg-ibtn sg-ibtn--quiet" title="Not now" aria-label="Hide for this signal" onClick={() => { hideAgent(id); setAgentOff(true); }}><SignalIcon id="x" size={14} /></button>
        </div>
      )}

      <div className="st-body">
        <div className="st-main">
          <div className="sg-fchips">
            {chips.map(([key, label]) => (
              <button key={key} className={`sg-chip ${activeChip === key ? "on" : ""}`} onClick={() => setChip(key)}>{key === "all" ? `All ${plural}` : label} · {counts[key]}</button>
            ))}
          </div>

          {isHiring ? (
            <HiringTable moves={shown} onRead={read} empty={emptyText} />
          ) : isCustom ? (
            <div className="sg-ftable cs-list">
              {shown.length === 0 && <div className="st-empty">{emptyText}</div>}
              {shown.map(m => {
                const c = customEventOf(m.item);
                return (
                  <div key={m.id} className={`cs-match ${m.unread ? "is-unread" : ""} ${m.needsYou ? "" : "is-done"}`}>
                    <span className="sg-frow__av">
                      {m.isAccount ? <AccountLogo domain={m.domain} name={c.recordName || m.name} /> : <ContactAvatar photo={m.photo} name={c.recordName || m.name} />}
                    </span>
                    <div className="sg-grow">
                      <div className="cs-match__head"><button type="button" className="sg-namelink" onClick={() => open(m)}>{c.recordName || m.name}</button><span className="st-sub">{m.happened}</span></div>
                      {c.why && <div className="cs-match__why">{c.why}</div>}
                      <Evidence evidence={c.evidence} />
                    </div>
                    {/* The name opens the record; only a real next step gets a button. */}
                    {m.isNew && m.main.kind !== "open" && (
                      <span className="st-act">
                        <button className="sg-btn sg-btn--small" onClick={() => act(m)}>{m.main.label}</button>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="st-table">
              <div className="st-row st-row--head">
                <span>Person</span><span>{isJobChange ? "Moved to" : "Company"}</span><span>Fit</span><span>What happened</span><span />
              </div>
              {shown.length === 0 && (
                <div className="st-empty">
                  {emptyText}
                </div>
              )}
              {shown.map(m => (
                <div key={m.id} className={`st-row ${m.unread ? "is-unread" : ""} ${m.needsYou ? "" : "is-done"}`}>
                  <span className="st-cell st-person">
                    <ContactAvatar photo={m.photo} name={m.name} />
                    <span className="st-stack">
                      <button type="button" className="sg-namelink st-name" onClick={() => open(m)}>{m.name}</button>
                      {m.title && <span className="st-sub">{m.title}</span>}
                    </span>
                  </span>
                  <span className="st-cell">
                    <CompanyLogo domain={m.toDomain} name={m.toCompany || m.fromCompany || m.name} />
                    <span className="st-stack">
                      <span className="st-name">{m.toCompany || m.headline}</span>
                      {m.fromCompany && <span className="st-sub">from {m.fromCompany}</span>}
                    </span>
                  </span>
                  <span className={`st-fit ${m.fit === true ? "is-fit" : ""} ${m.fit === null ? "is-unknown" : ""}`}>{m.fitText}</span>
                  <span className="st-what">{m.happened}</span>
                  <span className="st-act">
                    {m.isNew && m.main.kind !== "open" && <button className="sg-btn sg-btn--small" onClick={() => act(m)}>{actLabel(m)}</button>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="st-side">
          <section className="st-card">
            <div className="st-card__head">
              <span className="st-card__title">Tracking</span>
              {paused ? <span className="ag-pill ag-pill--draft">Paused</span> : <span className="ag-pill ag-pill--live"><span className="ag-dot" />Live</span>}
            </div>
            {faces.length > 0 && (
              <div className="st-faces" aria-label="People watched">
                {faces.map(f => <ContactAvatar key={f.entityId} photo={f.photo} name={f.name} />)}
                {more > 0 && <span className="st-faces__more">+{more}</span>}
              </div>
            )}
            {sellerProfileFor(view) && <div className="st-for">{sellerProfileFor(view)}</div>}
            {paused && view.pauseReason === "credit_cap" && <div className="st-note">Paused at its monthly credit cap. It picks up again next month, or resume it now.</div>}
            <dl className="st-facts">
              <div><dt>Watching</dt><dd>{watching != null ? `${watching.toLocaleString()} in ${scope}` : scope}</dd></div>
              <div>
                <dt>Checks <Info text={`Checked every ${s.checkEveryDays === 1 ? "day" : s.checkEveryDays === 7 ? "week" : s.checkEveryDays === 30 ? "month" : `${s.checkEveryDays} days`}. Last check: ${lastCheck ? shortDate(lastCheck) : "not yet"}.`} /></dt>
                <dd>{everyLabel(s.checkEveryDays)}</dd>
              </div>
              {nextCheck && <div><dt>Next check</dt><dd>{nextCheck}</dd></div>}
              {isCustom ? (
                <div><dt>Cost <Info text={cost[0]} /></dt><dd>{cost.length > 1 ? cost[1].replace(/^about /, "~") : cost[0]}</dd></div>
              ) : (
                <div>
                  <dt>Each check {view.creditCap ? <Info text={`Up to ${view.creditCap.toLocaleString()} credits a month. It pauses at the cap and picks up next month.`} /> : null}</dt>
                  <dd>{!estimate ? "…" : estimate.creditsPerCheck != null ? `${estimate.creditsPerCheck.toLocaleString()} credits` : "Free"}</dd>
                </div>
              )}
              {view.creditsUsedTotal > 0 && <div><dt>Credits used</dt><dd>{view.creditsUsedTotal.toLocaleString()}{view.creditsUsedThisMonth > 0 ? ` · ${view.creditsUsedThisMonth.toLocaleString()} this month` : ""}</dd></div>}
            </dl>
            <div className={`st-card__acts${canCheckNow ? " has-check" : ""}`}>
              {(definition || isCustom) && <button className="sg-btn sg-btn--ghost sg-btn--small" title="Edit" aria-label="Edit" onClick={() => setEditing(true)}><i className="material-icons-outlined" aria-hidden="true">edit</i><span className="st-card__lbl">Edit</span></button>}
              <button className="sg-btn sg-btn--ghost sg-btn--small" title={paused ? "Resume" : "Pause"} aria-label={paused ? "Resume" : "Pause"} disabled={busy} onClick={togglePause}><i className="material-icons-outlined" aria-hidden="true">{paused ? "play_arrow" : "pause"}</i><span className="st-card__lbl">{paused ? "Resume" : "Pause"}</span></button>
              {canCheckNow && <button className="sg-btn sg-btn--ghost sg-btn--small" title="Check now" aria-label="Check now" disabled={checking} onClick={checkNow}><i className="material-icons-outlined" aria-hidden="true">refresh</i><span className="st-card__lbl">{checking ? "Checking…" : "Check now"}</span></button>}
              <button className="sg-link sg-link--danger sg-link--icon st-stop" title="Stop tracking" aria-label="Stop tracking" onClick={stop}><i className="material-icons-outlined" aria-hidden="true">block</i><span className="st-card__lbl">Stop tracking</span></button>
            </div>
          </section>

          {isCustom && (
            <section className="st-card cs-def">
              <div className="st-card__head">
                <span className="st-card__title sg-grow">What it watches for</span>
                <button className="sg-link" onClick={() => setEditing(true)}>Edit</button>
              </div>
              <dl>
                <div><dt>Fires when</dt><dd>{cdef.condition || "Not set"}</dd></div>
                {(cdef.keywords || []).length > 0 && (
                  <div><dt>Words we search for</dt><dd className="cs-kws">{cdef.keywords.map(k => <span key={k} className="sg-atag">{k}</span>)}</dd></div>
                )}
                {sourcesText(cdef.sources) && <div><dt>Where we look</dt><dd className="cs-cap">{sourcesText(cdef.sources)}</dd></div>}
                <div><dt>Who</dt><dd>{scope}</dd></div>
              </dl>
            </section>
          )}

        </aside>
      </div>

      <AutomateHost onChange={async () => setView(await fetchTracked(id))} />
      {editing && isCustom && <CustomSignalDrawer saved={view} onClose={closeCustom} />}
      {editing && definition && !isCustom && <SignalSetupDrawer definition={definition} saved={view} onClose={closeDrawer} />}
    </div>
  );
};

export default observer(SignalTrackerPage);
